import React, { useState, useEffect } from 'react';
import { cleanCategoryName, formatYAxisNumber } from '../utils';
import { useNavigate } from 'react-router-dom';

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';

// ─── STAT CARD (compact version) ─────────────────────────────────────────────
const StatCard = ({ label, value, sub, change, changeColor, icon }) => {
  const isPositive = changeColor === '#27ae60';
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 12,
      padding: '18px 20px',
      flex: 1,
      minWidth: 200,
      transition: 'box-shadow 0.25s ease, transform 0.2s ease',
      cursor: 'default',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 14, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{label}</div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{sub}</div>
      {change && (
        <div style={{ fontSize: 13, color: changeColor || '#27ae60', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ fontSize: 12 }}>{isPositive ? '▲' : '▼'}</span> {change}
        </div>
      )}
    </div>
  );
};

// ─── PIE LABEL LAYOUT ─────────────────────────
const RADIAN = Math.PI / 180;

const buildPieLabelLayout = (data, cx, cy, outerRadius) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const labelRadius = outerRadius + 28;
  const bendOffset = 12;
  const minGap = 15;

  let cumulative = 0;
  const items = data.map((d) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 360;
    const midAngle = (startAngle + endAngle) / 2;
    const percent = d.value / total;

    const edgeX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const edgeY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const rawY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const side = Math.cos(-midAngle * RADIAN) >= 0 ? 'right' : 'left';

    return { name: d.name, color: d.color, percent, edgeX, edgeY, rawY, side };
  });

  ['left', 'right'].forEach((side) => {
    const group = items
      .map((item, i) => ({ ...item, i }))
      .filter((item) => item.side === side)
      .sort((a, b) => a.rawY - b.rawY);

    for (let k = 1; k < group.length; k++) {
      if (group[k].rawY - group[k - 1].rawY < minGap) {
        group[k].rawY = group[k - 1].rawY + minGap;
      }
    }
    group.forEach((item) => { items[item.i].finalY = item.rawY; });
  });

  items.forEach((item) => {
    if (item.finalY === undefined) item.finalY = item.rawY;
    item.finalX = item.side === 'right' ? cx + labelRadius : cx - labelRadius;
    item.bendX = item.side === 'right' ? cx + outerRadius + bendOffset : cx - outerRadius - bendOffset;
  });

  return items;
};

// ─── LOADING SKELETON ────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={{
    background: '#fff',
    border: '1px solid #e8edf2',
    borderRadius: 12,
    padding: '18px 20px',
    flex: 1,
    minWidth: 0,
  }}>
    <div style={{ width: '60%', height: 10, background: '#f0f0f0', borderRadius: 4, marginBottom: 12 }} />
    <div style={{ width: '40%', height: 24, background: '#f0f0f0', borderRadius: 4, marginBottom: 8 }} />
    <div style={{ width: '50%', height: 10, background: '#f0f0f0', borderRadius: 4 }} />
  </div>
);

// ─── REUSABLE NO-DATA MESSAGE ────────────────────────────────────────────────
const NoDataMessage = ({ message = "No data available" }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: '#aaa',
    fontSize: 14,
    fontWeight: 500,
    minHeight: 160,
  }}>
    {message}
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const CustomerHomeSummary = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('1yr');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = ['1mo', '3mo', '6mo', '1yr'];
  const filterLabels = { '1mo': 'Last Month', '3mo': 'Last 3 Months', '6mo': 'Last 6 Months', '1yr': 'Last Year' };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const API = 'https://searchmytenders.in';
        const res = await fetch(`${API}/api/customer-analytics/kpis?filter=${activeFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setKpiData(data.kpiData);
          if (data.chartData) {
            let parsedData = data.chartData;
            if (activeFilter === '1yr' || activeFilter === '6mo' || activeFilter === '3mo') {
              const monthsCount = activeFilter === '1yr' ? 12 : (activeFilter === '6mo' ? 6 : 3);
              const now = new Date();
              const padded = [];
              for (let i = monthsCount - 1; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const label = d.toLocaleString('en-US', { month: 'short' });
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, Tracked: 0, Won: 0 });
              }
              parsedData = padded;
            } else if (activeFilter === '1mo') {
              const now = new Date();
              const padded = [];
              const startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
              const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
              for (let i = daysDiff; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
                const label = `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`;
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, Tracked: 0, Won: 0 });
              }
              parsedData = padded;
            }
            setChartData(parsedData);
          }
          if (data.pieChartData) setCategoryData(data.pieChartData);
        } else {
          setError(data.message || 'Failed to load data');
        }
      } catch (err) {
        console.error('Failed to fetch customer home data', err);
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeFilter]);

  const getTotal = (data) => data.reduce((sum, d) => sum + d.value, 0);

  const renderCustomLabel = (props) => {
    const { cx, cy, outerRadius, index, percent } = props;
    if (percent < 0.005) return null;

    const layout = buildPieLabelLayout(categoryData, cx, cy, outerRadius);
    const pos = layout[index];
    if (!pos) return null;

    const _n = cleanCategoryName(pos.name); const shortName = _n.length > 16 ? _n.slice(0, 14) + '…' : _n;
    const anchor = pos.side === 'right' ? 'start' : 'end';
    const textX = pos.finalX + (pos.side === 'right' ? 6 : -6);

    return (
      <g>
        <polyline
          points={`${pos.edgeX},${pos.edgeY} ${pos.bendX},${pos.finalY} ${pos.finalX},${pos.finalY}`}
          fill="none"
          stroke="#c7c7c7"
          strokeWidth={1}
        />
        <circle cx={pos.finalX} cy={pos.finalY} r={2} fill={pos.color || '#999'} />
        <text x={textX} y={pos.finalY} textAnchor={anchor} dominantBaseline="central" style={{ fontSize: 15 }}>
          <tspan fill="#555">{shortName}</tspan>
          <tspan fill="#1a1a2e" fontWeight={700}>{`  ${(pos.percent * 100).toFixed(0)}%`}</tspan>
        </text>
      </g>
    );
  };

  return (
    <div style={{
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '28px 32px 20px',
      maxWidth: 1400,
      margin: '0 auto',
    }}>

      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;
        }
      `}</style>

      {/* ── HEADER + TIME FILTER ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <h4 style={{
            fontWeight: 700,
            fontSize: 22,
            marginBottom: 4,
            color: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            My Performance Overview
          </h4>
          <p style={{ fontSize: 15, color: '#444', margin: 0 }}>
            Key metrics for your tenders — {filterLabels[activeFilter]}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {filters.map(f => (
            <button
              key={f}
              onClick={() => {
                if (activeFilter !== f) {
                  setLoading(true);
                  setActiveFilter(f);
                }
              }}
              style={{
                padding: '6px 18px',
                border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                borderRadius: 50,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: activeFilter === f ? '#e0f5f3' : '#fff',
                color: activeFilter === f ? '#00897b' : '#888',
                transition: 'all 0.2s',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI STAT CARDS ── */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : error ? (
          <div style={{ padding: '20px', color: '#e74c3c', fontSize: 14, fontWeight: 'bold' }}>Error: {error}</div>
        ) : !kpiData ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '30px 0', color: '#aaa', fontSize: 14, fontWeight: 500 }}>
            No data available
          </div>
        ) : (
          <>
            <StatCard
              label="Total Tenders Tracked"
              value={kpiData.tendersListed.value}
              sub="This period"
              change={`${Math.abs(kpiData.tendersListed.change)}% vs last period`}
              changeColor={kpiData.tendersListed.change >= 0 ? "#27ae60" : "#e74c3c"}
            />
            <StatCard
              label="Active Opportunities"
              value={kpiData.activeOpportunities.value}
              sub="Pending or Shortlisted"
              change={`${Math.abs(kpiData.activeOpportunities.change)}% vs last period`}
              changeColor={kpiData.activeOpportunities.change >= 0 ? "#27ae60" : "#e74c3c"}
            />
            <StatCard
              label="Tenders Won"
              value={kpiData.awardedTenders.value}
              sub="Approved tenders"
              change={`${Math.abs(kpiData.awardedTenders.change)}% vs last period`}
              changeColor={kpiData.awardedTenders.change >= 0 ? "#27ae60" : "#e74c3c"}
              icon="🏆"
            />
            <StatCard
              label="Total Won Value"
              value={kpiData.wonWorth.value}
              sub="Awarded tenders"
              change={`${Math.abs(kpiData.wonWorth.change)}% vs last period`}
              changeColor={kpiData.wonWorth.change >= 0 ? "#27ae60" : "#e74c3c"}
              icon="💰"
            />
            <StatCard
              label="Lost Tenders"
              value={kpiData.lostTenders.value}
              sub="Rejected tenders"
              change={`${Math.abs(kpiData.lostTenders.change)}% vs last period`}
              changeColor={kpiData.lostTenders.change <= 0 ? "#27ae60" : "#e74c3c"}
              icon="❌"
            />
            <StatCard
              label="True Win Rate"
              value={kpiData.winRate.value}
              sub="Awarded vs Lost"
              change={`${Math.abs(kpiData.winRate.change)}% change`}
              changeColor={kpiData.winRate.change >= 0 ? "#27ae60" : "#e74c3c"}
              icon="🚀"
            />
          </>
        )}
      </div>

      {/* ── CHARTS ROW: AREA + PIE ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>

        {/* Area Chart — Tender Activity */}
        <div style={{ flex: '1 1 58%', minWidth: '240px', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>
            {activeFilter === '1yr' ? 'Yearly Tender Activity' : 'Monthly Tender Activity'}
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Tenders tracked and won over time</div>
          <div style={{ flex: 1, minHeight: 200 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : chartData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="homeColorListed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1565C0" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1565C0" stopOpacity={0.3} />
                    </linearGradient>
                    <linearGradient id="homeColorOpp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#27ae60" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#27ae60" stopOpacity={0.25} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ dy: 10, fontSize: 16, fill: '#777', fontWeight: 500 }} axisLine={false} tickLine={false} interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                  <YAxis tickFormatter={formatYAxisNumber} tick={{ dx: -10, fontSize: 16, fill: '#777', fontWeight: 500 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 13, fontWeight: 500, borderRadius: 6, border: '1px solid #e0e0e0' }} />
                  <Legend iconSize={18} wrapperStyle={{ fontSize: 14, paddingTop: 8, fontWeight: 500 }} formatter={(value) => <span style={{ color: '#444' }}>{value}</span>} />
                  <Area type="monotone" dataKey="Tracked" stroke="#1565C0" strokeWidth={2} fill="url(#homeColorListed)" dot={false} />
                  <Area type="monotone" dataKey="Won" stroke="#27ae60" strokeWidth={2} fill="url(#homeColorOpp)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Pie Chart — Tenders by Status */}
        <div style={{ flex: '1 1 38%', minWidth: '240px', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Tenders by Status</div>
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 4 }}>Distribution of your active tenders</div>
          <div style={{ flex: 1, minHeight: 230 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : categoryData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 16, right: 65, left: 65, bottom: 16 }}>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    labelLine={false}
                    label={renderCustomLabel}
                    isAnimationActive={false}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} Tenders`, name]} contentStyle={{ fontSize: 12, fontWeight: 500, borderRadius: 6 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Legend */}
          {!loading && categoryData.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
              {[...categoryData]
                .sort((a, b) => b.value - a.value)
                .map(c => {
                  const total = getTotal(categoryData);
                  const pct = total ? ((c.value / total) * 100).toFixed(0) : 0;
                  return (
                    <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                      <span style={{ color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cleanCategoryName(c.name)}</span>
                      <span style={{ color: '#333', fontWeight: 600 }}>{pct}%</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* ── VIEW FULL ANALYTICS CTA ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: 8,
        marginBottom: 12,
      }}>
        <button
          onClick={() => navigate('/customer-analytics')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 32px',
            background: '#2F7F8F',
            color: '#fff',
            border: 'none',
            borderRadius: '20px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          View Full Analytics
        </button>
      </div>

      {/* Spinner keyframe */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CustomerHomeSummary;